/**
 * Mixed Strategy Example: BTN vs BB 3-bet Defense - Compact Grid Format
 *
 * BTN opens, BB 3-bets, BTN decides how to respond
 * Actions: Fold, Call, 4-bet
 *
 * Note: This is a simplified example for demonstration purposes.
 * Real GTO strategies are more nuanced and position-dependent.
 */

import type { ChartData } from '@/types/chart';

export const mixedStrategyData: ChartData = {
  version: 1,
  name: 'BTN vs BB 3-bet Defense',
  description: 'Mixed strategy frequencies for each action (4-bet/Call/Fold)',
  categoryMapping: {
    categories: ['4-bet', 'Call', 'Fold'],
    direction: 'descending',
  },
  hands: [
    // Row 0 (A)
    [
      [
        { value: 0.8, label: '4-bet' },
        { value: 0.2, label: 'Call' },
      ],
      [
        { value: 0.7, label: '4-bet' },
        { value: 0.3, label: 'Call' },
      ],
      [
        { value: 0.4, label: '4-bet' },
        { value: 0.6, label: 'Call' },
      ],
      [
        { value: 0.3, label: '4-bet' },
        { value: 0.7, label: 'Call' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.7, label: 'Call' },
        { value: 0.1, label: 'Fold' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.3, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.3, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.3, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.3, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
    ],

    // Row 1 (K)
    [
      [
        { value: 0.5, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.1, label: 'Fold' },
      ],
      [
        { value: 0.5, label: '4-bet' },
        { value: 0.5, label: 'Call' },
      ],
      [
        { value: 0.3, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.1, label: 'Fold' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.2, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.3, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [
        { value: 0.05, label: 'Call' },
        { value: 0.95, label: 'Fold' },
      ],
      [
        { value: 0.05, label: 'Call' },
        { value: 0.95, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
    ],

    // Row 2 (Q)
    [
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.7, label: 'Call' },
        { value: 0.2, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.3, label: 'Fold' },
      ],
      [
        { value: 0.4, label: '4-bet' },
        { value: 0.6, label: 'Call' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.3, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
    ],

    // Row 3 (J)
    [
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.3, label: '4-bet' },
        { value: 0.7, label: 'Call' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
    ],

    // Row 4 (T)
    [
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.2, label: '4-bet' },
        { value: 0.7, label: 'Call' },
        { value: 0.1, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
    ],

    // Row 5 (9)
    [
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.7, label: 'Call' },
        { value: 0.2, label: 'Fold' },
      ],
      [
        { value: 0.5, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
    ],

    // Row 6 (8)
    [
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.6, label: 'Call' },
        { value: 0.3, label: 'Fold' },
      ],
      [
        { value: 0.4, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
    ],

    // Row 7 (7)
    [
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [{ value: 1, label: 'Fold' }],
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.5, label: 'Call' },
        { value: 0.4, label: 'Fold' },
      ],
      [
        { value: 0.3, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
    ],

    // Row 8 (6)
    [
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
      null,
      null,
      null,
      null,
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.2, label: 'Call' },
        { value: 0.8, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
    ],

    // Row 9 (5)
    [
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.4, label: 'Call' },
        { value: 0.5, label: 'Fold' },
      ],
      [
        { value: 0.1, label: 'Call' },
        { value: 0.9, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
    ],

    // Row 10 (4)
    [
      [{ value: 1, label: 'Fold' }],
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.3, label: 'Call' },
        { value: 0.6, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
      null,
    ],

    // Row 11 (3)
    [
      [{ value: 1, label: 'Fold' }],
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.2, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
      [{ value: 1, label: 'Fold' }],
    ],

    // Row 12 (2)
    [
      [{ value: 1, label: 'Fold' }],
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      [
        { value: 0.1, label: '4-bet' },
        { value: 0.2, label: 'Call' },
        { value: 0.7, label: 'Fold' },
      ],
    ],
  ],
};
